import { useEffect, useMemo, useRef, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import * as Toast from '@radix-ui/react-toast'
import { useSoundActions } from 'src/store/sound'
import { isLobbyPage } from '../../utils/isLobbyPage'
import { useActiveNotifications, useNotificationActions } from '../../store'
import { NotificationMessage } from '../NotificationMessage'
import { NOTIFICATION_CONFIG } from '../../config'
import { toastViewportVariants } from './NotificationsStack.variants'

export function NotificationsStack() {
  const notifications = useActiveNotifications()
  const { removeNotification, hideNotification } = useNotificationActions()
  const { playEffect } = useSoundActions()
  const previousNotificationCount = useRef(0)
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const autoDismissTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const pathname = usePathname()

  const isLobby = useMemo(() => isLobbyPage(pathname), [pathname])

  useEffect(() => {
    const currentCount = notifications.length
    if (currentCount > previousNotificationCount.current) {
      playEffect('bonus')
    }
    previousNotificationCount.current = currentCount
  }, [notifications.length, playEffect])

  // Cleanup all timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutsRef.current
    const autoDismissTimeouts = autoDismissTimeoutsRef.current
    return () => {
      timeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId)
      })
      timeouts.clear()
      autoDismissTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId)
      })
      autoDismissTimeouts.clear()
    }
  }, [])

  const handleClose = useCallback(
    (id: string) => {
      hideNotification(id)

      // Clear any existing timeout for this notification
      const existingTimeout = timeoutsRef.current.get(id)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      // Remove after animation
      const timeoutId = setTimeout(() => {
        removeNotification(id)
        timeoutsRef.current.delete(id)
      }, NOTIFICATION_CONFIG.REMOVE_ANIMATION_DURATION_MS)

      timeoutsRef.current.set(id, timeoutId)
    },
    [hideNotification, removeNotification]
  )

  // Deterministic auto-dismiss. Radix's own duration timer pauses when the
  // embedded widget loses focus or on hover, which can leave finite-duration
  // banners (e.g. the free-bet grant banner) on screen indefinitely. This
  // focus-independent backup guarantees they close after their duration.
  useEffect(() => {
    notifications.forEach((notification) => {
      const duration = notification.duration ?? NOTIFICATION_CONFIG.DEFAULT_DURATION_MS
      if (!notification.visible || !Number.isFinite(duration)) return
      if (autoDismissTimeoutsRef.current.has(notification.id)) return

      const timeoutId = setTimeout(() => {
        autoDismissTimeoutsRef.current.delete(notification.id)
        handleClose(notification.id)
      }, duration)
      autoDismissTimeoutsRef.current.set(notification.id, timeoutId)
    })

    // Drop timers for notifications that are no longer present.
    const liveIds = new Set(notifications.map((notification) => notification.id))
    autoDismissTimeoutsRef.current.forEach((timeoutId, id) => {
      if (!liveIds.has(id)) {
        clearTimeout(timeoutId)
        autoDismissTimeoutsRef.current.delete(id)
      }
    })
  }, [notifications, handleClose])

  if (notifications.length === 0) {
    return null
  }

  return (
    <Toast.Provider swipeDirection="up">
      {notifications.map((notification) => (
        <Toast.Root
          className="pointer-events-auto transition-all duration-300 ease-out data-[state=open]:animate-slide-in-down data-[state=closed]:-translate-y-full data-[state=closed]:opacity-0 data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:transition-transform data-[swipe=end]:animate-swipe-out"
          duration={notification.duration ?? NOTIFICATION_CONFIG.DEFAULT_DURATION_MS}
          key={notification.id}
          onOpenChange={(open) => {
            if (!open) {
              handleClose(notification.id)
            }
          }}
          open={notification.visible}
        >
          <NotificationMessage
            notification={notification}
            onClose={() => {
              handleClose(notification.id)
            }}
          />
        </Toast.Root>
      ))}
      <Toast.Viewport className={toastViewportVariants({ isLobby })} />
    </Toast.Provider>
  )
}
